/*
 * ⛔ The monthly budget's bounds, in GBP: the API's own (backend/src/services/cloud/budgetBounds.ts,
 * BUDGET_MANDATORY_PLAN_2026-10-28 section 5.1). The console cannot import them, so they are repeated here for the two
 * places that take a budget, BudgetSettings and the card form (SetupBillingModal), and
 * backend/tests/budget-bounds-console.test.ts checks these literals, and the range both of them show, against the API's.
 */
// £0 is a budget, not "no budget": free tier only (Milos, 2026-10-29; backend/src/services/cloud/budgetBounds.ts).
export const BUDGET_MIN = 0;
export const BUDGET_MAX = 1_000_000;

/** An amount in pounds, as every budget line shows it: "£1,234.50". */
export const money = (value: number) => new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(value);

/*
 * ⛔ What a reached budget does (Milos, 2026-10-28: "Just 2 buttons. Save budget and continue and Save hard budget and
 * continue"), the API's BUDGET_MODES (backend/src/services/cloud/budgetBounds.ts), stored as billing_customers.budget_hard:
 *  - soft, the default: at 100% no new resources and no resizing up; running resources keep running and billing.
 *  - hard: the same, and every billable resource is deleted, to stop further billing. Usage is evaluated hourly, so
 *    that is within about an hour of the budget being reached, never "the moment".
 */
export type BudgetMode = 'soft' | 'hard';

/**
 * A mode as the API sends it: hard only for a literal "hard". A reply without the field comes from a server that
 * predates the modes, which saves every budget soft, so anything else reads soft, never hard.
 */
export const budgetModeOf = (value: unknown): BudgetMode => (value === 'hard' ? 'hard' : 'soft');

/*
 * ⛔ A hard budget is confirmed by typing IACCEPTTHEDELETIONRISK (Milos, 2026-10-29: "instead of just type DELETE. Make it type: IACCEPTTHEDELETIONRISK"),
 * trimmed, in any letter case; anything else confirms nothing. The API's own rule
 * (backend/src/services/cloud/budgetBounds.ts confirmsHardBudget), which the console cannot import: every PUT with mode
 * hard sends the word as hard_confirm, or the API answers 400 hard_budget_confirmation_required and saves nothing.
 * backend/tests/budget-bounds-console.test.ts checks this word against the API's.
 */
export const HARD_BUDGET_CONFIRM_WORD = 'IACCEPTTHEDELETIONRISK';
export const confirmsHardBudget = (typed: string) => typed.trim().toUpperCase() === HARD_BUDGET_CONFIRM_WORD;

/**
 * The question before saving a budget at or below this month's spend, which is reached the moment it is saved. One
 * wording for BudgetSettings and the card form (plan sections 5.4 and 10). A hard budget saved there is evaluated at
 * once, so its deletion starts on the save, not within the hour, and its words never say running resources keep
 * running. The card form saves a soft budget only, so it asks in the soft words.
 */
export const BUDGET_REACHED_TITLE = 'This budget is already reached';
// Milos, 2026-10-29: "If there is usage. I want text that already used usage is still going to be billed. Even with hard
// budget to 0". The period is the budget's (a card account's invoice period), so its reset date, never "the next month".
export const budgetReachedMessage = (spent: number, limit: number, mode: BudgetMode = 'soft', resets?: string) => {
    const used = spent > 0 ? `You have already used ${money(spent)} this period, and it is still billed${mode === 'hard' ? ', even with a hard budget' : ''}. ` : '';
    return mode === 'hard'
        ? `${used}Saving a ${money(limit)} hard budget starts deleting all billable resources now. This cannot be undone.`
        : `${used}With a ${money(limit)} budget, you cannot create new resources until you raise it or the period resets${resets ? ` on ${resets}` : ''}. Running resources keep running.`;
};
